import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";
import { EASE, prefersReducedMotion } from "../../lib/motion";

const PRIMARY = [
  { to: "/", label: "Home", end: true },
  { to: "/firm", label: "Firm" },
  { to: "/investment-strategy", label: "Investment Strategy" },
  { to: "/letters", label: "Letters" },
  { to: "/leadership", label: "Leadership" },
  { to: "/global-presence", label: "Global Presence" },
  { to: "/contact", label: "Contact" },
];

const PRIVATE_ACCESS = [
  { to: "/investor-portal", label: "Investor Portal", subtitle: "By Invitation" },
  { to: "/careers", label: "Careers", subtitle: "Exceptional people only." },
];

const CITIES = ["United States", "Luxembourg", "Dubai"];

const linkTestId = (to) =>
  `menu-link-${to.replace(/^\//, "").replace(/\//g, "-") || "home"}`;

const PrimaryItem = ({ item, idx, reduce, baseDelay }) => (
  <motion.li
    initial={reduce ? false : { opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{
      duration: reduce ? 0 : 0.45,
      ease: EASE,
      delay: reduce ? 0 : baseDelay + idx * 0.06,
    }}
  >
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={linkTestId(item.to)}
      className="group/link relative inline-flex items-center gap-4 font-serif text-offwhite hover:text-offwhite transition-opacity duration-300 group-hover/list:opacity-30 hover:!opacity-100"
      style={{ fontSize: "clamp(28px, 3vw, 40px)", lineHeight: 1.1, letterSpacing: "-0.005em" }}
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className="block bg-gold shrink-0 transition-opacity duration-300"
            style={{ width: 20, height: 1, opacity: isActive ? 1 : 0, marginRight: 4 }}
          />
          <span className="relative inline-block">
            {item.label}
            <span
              aria-hidden="true"
              className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-[250ms] ease-out group-hover/link:scale-x-100"
            />
          </span>
        </>
      )}
    </NavLink>
  </motion.li>
);

const PrivateItem = ({ item, idx, reduce, baseDelay }) => (
  <motion.li
    initial={reduce ? false : { opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{
      duration: reduce ? 0 : 0.45,
      ease: EASE,
      delay: reduce ? 0 : baseDelay + idx * 0.06,
    }}
  >
    <NavLink
      to={item.to}
      data-testid={linkTestId(item.to)}
      className="group/link block transition-opacity duration-300 group-hover/list:opacity-30 hover:!opacity-100"
    >
      <span
        className="relative inline-block font-serif text-offwhite"
        style={{ fontSize: "clamp(22px, 2.2vw, 28px)", lineHeight: 1.15, letterSpacing: "-0.005em" }}
      >
        {item.label}
        <span
          aria-hidden="true"
          className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-[250ms] ease-out group-hover/link:scale-x-100"
        />
      </span>
      <p
        className="mt-1 font-serif italic text-offwhite/55"
        style={{ fontSize: "14px", lineHeight: 1.4 }}
      >
        {item.subtitle}
      </p>
    </NavLink>
  </motion.li>
);

export const MenuOverlay = ({ open, onClose }) => {
  const location = useLocation();
  const reduce = prefersReducedMotion();
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Body scroll lock + Esc + return focus to the hamburger trigger on close.
  useEffect(() => {
    if (!open) return;
    const triggerEl = document.querySelector('[data-testid="menu-trigger"]');
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus into the drawer once it opens.
    const t = window.setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 120);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (triggerEl && typeof triggerEl.focus === "function") {
        triggerEl.focus();
      }
    };
  }, [open, onClose]);

  // Simple focus trap: cycle Tab / Shift+Tab within the drawer while open.
  useEffect(() => {
    if (!open) return;
    const trap = (e) => {
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", trap);
    return () => window.removeEventListener("keydown", trap);
  }, [open]);

  // Auto-close on route change.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Stagger anchors for content inside the drawer.
  const D0 = 0.15; // NAVIGATE eyebrow
  const primaryStart = D0 + 0.08;
  const privateEyebrowDelay = primaryStart + PRIMARY.length * 0.06 + 0.15;
  const privateStart = privateEyebrowDelay + 0.08;
  const chairmanDelay = privateStart + PRIVATE_ACCESS.length * 0.06 + 0.15;

  const drawerTransition = { duration: reduce ? 0 : 0.45, ease: EASE };
  const scrimTransition = { duration: reduce ? 0 : 0.3, ease: EASE };

  return (
    <AnimatePresence>
      {open ? (
        <>
          {/* Scrim — softer dimming so the underlying page reads through. Click closes. */}
          <motion.div
            key="scrim"
            aria-hidden="true"
            data-testid="menu-scrim"
            onClick={onClose}
            className="fixed inset-0 z-[99] bg-navy/40"
            style={{ backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={scrimTransition}
          />

          {/* Drawer — slides in from the right, translucent navy, no edge border. */}
          <motion.aside
            key="drawer"
            ref={panelRef}
            data-testid="menu-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            className="fixed inset-y-0 right-0 z-[100] bg-navy/25 overflow-y-auto w-[88vw] md:w-[60vw] lg:w-[32vw] lg:max-w-[560px] lg:min-w-[480px]"
            style={{
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
            }}
            initial={reduce ? false : { x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={drawerTransition}
          >
            {/* Close row */}
            <div className="flex items-center justify-end px-8 sm:px-10 lg:px-12 pt-6 sm:pt-8">
              <button
                ref={closeBtnRef}
                type="button"
                data-testid="menu-close"
                onClick={onClose}
                className="group inline-flex items-center gap-2 font-sans uppercase text-offwhite/85 hover:text-offwhite transition-colors"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                aria-label="Close menu"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" strokeWidth="1" />
                </svg>
                <span className="relative">
                  Close
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-[250ms] ease-out"
                  />
                </span>
              </button>
            </div>

            <div className="px-8 sm:px-10 lg:px-12 pt-10 pb-16">
              {/* NAVIGATE eyebrow */}
              <motion.p
                className="font-sans uppercase text-offwhite/55"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                initial={reduce ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: reduce ? 0 : 0.4, ease: EASE, delay: reduce ? 0 : D0 }}
              >
                Navigate
              </motion.p>

              <nav aria-label="Primary" className="mt-6">
                <ul className="flex flex-col gap-3 sm:gap-4 group/list">
                  {PRIMARY.map((item, i) => (
                    <PrimaryItem
                      key={item.to}
                      item={item}
                      idx={i}
                      reduce={reduce}
                      baseDelay={primaryStart}
                    />
                  ))}
                </ul>
              </nav>

              {/* Divider */}
              <div
                className="mt-10 pt-6"
                style={{ borderTop: "1px solid rgba(245,244,242,0.15)" }}
              />

              {/* PRIVATE ACCESS */}
              <motion.p
                className="font-sans uppercase text-offwhite/55"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                initial={reduce ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: reduce ? 0 : 0.4,
                  ease: EASE,
                  delay: reduce ? 0 : privateEyebrowDelay,
                }}
              >
                Private Access
              </motion.p>

              <nav aria-label="Private access" className="mt-6">
                <ul className="flex flex-col gap-8 group/list">
                  {PRIVATE_ACCESS.map((item, i) => (
                    <PrivateItem
                      key={item.to}
                      item={item}
                      idx={i}
                      reduce={reduce}
                      baseDelay={privateStart}
                    />
                  ))}
                </ul>
              </nav>

              {/* Divider */}
              <div
                className="mt-12 pt-6"
                style={{ borderTop: "1px solid rgba(245,244,242,0.15)" }}
              />

              {/* Office of the Chairman */}
              <motion.div
                initial={reduce ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: reduce ? 0 : 0.4,
                  ease: EASE,
                  delay: reduce ? 0 : chairmanDelay,
                }}
              >
                <p
                  className="font-sans uppercase text-offwhite/55"
                  style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                >
                  Office of the Chairman
                </p>
                <a
                  href="mailto:chairman@quscap.com"
                  data-testid="menu-chairman-email"
                  className="group/em mt-4 inline-block font-serif text-offwhite"
                  style={{ fontSize: "clamp(18px, 1.6vw, 22px)" }}
                >
                  <span className="relative">
                    chairman@quscap.com
                    <span
                      aria-hidden="true"
                      className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-[250ms] ease-out group-hover/em:scale-x-100"
                    />
                  </span>
                </a>
                <ul
                  className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 font-sans uppercase text-offwhite/65"
                  style={{ fontSize: "11px", letterSpacing: "0.2em" }}
                >
                  {CITIES.map((c, i) => (
                    <li key={c} className="inline-flex items-center gap-3">
                      <span>{c}</span>
                      {i < CITIES.length - 1 ? (
                        <span
                          aria-hidden="true"
                          className="inline-block bg-gold"
                          style={{ width: 4, height: 4 }}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
                <div className="mt-5">
                  <a
                    href="#"
                    data-testid="menu-linkedin"
                    className="group/li font-sans uppercase text-offwhite/65 hover:text-offwhite transition-colors"
                    style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                  >
                    <span className="relative">
                      LinkedIn
                      <span
                        aria-hidden="true"
                        className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover/li:scale-x-100 transition-transform duration-[250ms] ease-out"
                      />
                    </span>
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
};

export default MenuOverlay;
